/**
 * What each protocol step is actually configured with.
 *
 * Every value here was read out of the OpenADS source tree, not out of the
 * documentation, because the two disagree. `configs/dwi_pipeline.yaml` reads
 * like the pipeline's control panel, but `scripts/run_ads_dwi.py` only ever
 * looks up six keys in it; the rest of the file is decoration. Meanwhile the
 * numbers that decide how registration behaves live in a Python dataclass, and
 * `configs/reg_params.yaml` — the file whose name promises exactly those
 * numbers — is read by nothing at all.
 *
 * What the runner loads is a merge, not a file. `ads/core/config.py` defines
 * `load_config` twice — at line 22, a bare `yaml.safe_load`, and again at line
 * 301, which is the one Python keeps. The second reads `defaults.yaml` from the
 * pipeline config's own directory, deep-merges the pipeline config over it, and
 * expands `${PROJECT_ROOT}`. So `paths.*` reaches the runner from
 * `defaults.yaml` even though no pipeline config mentions it, and a key is
 * sourced below to the file it is actually written in.
 *
 * So every parameter carries an `origin` saying where the running value comes
 * from, and a `source` naming the file (and key, or line) it was read from:
 *
 *   config   the runner reads this key from the YAML at run time. Editing the
 *            YAML changes the run.
 *   fixed    written into the runner script. A YAML key of the same name may
 *            exist and be ignored — where that is the case, `note` says so.
 *   default  neither the runner nor the YAML sets it; this is the default of
 *            the dataclass or library underneath, and is what runs.
 *
 * A `config` parameter can carry an `edit` block, and those are the ones the
 * panel lets a reader change: the value is sent with the run as a dotted path
 * and applied to the config the runner loads. `fixed` and `default` values are
 * shown but never editable, which is the point rather than a caveat — a field
 * that accepted a number and then did nothing with it would be worse than no
 * field, because the reader would attribute the result to their number.
 *
 * To re-derive this table against another OpenADS checkout:
 *   grep -n 'cfg\.get\|cfg\[' scripts/run_ads_dwi.py scripts/run_ads_pwi.py
 * Anything not in that output is `fixed` or `default`, whatever the YAML says.
 */

import type { Modality, ParamValue, StageId } from "@/ipc/contract";

export type ParamOrigin = "config" | "fixed" | "default";

/** How a settable parameter is entered. */
export type ParamKind = "number" | "boolean" | "text" | "numbers" | "enum";

export interface ParamEdit {
  /** Dotted path into the config the runner loads, e.g. `data_raw.bvalue`. */
  key: string;
  kind: ParamKind;
  /** The value the pipeline uses when the reader has not changed it. */
  fallback: ParamValue;
  /** For `enum`. */
  choices?: string[];
  /** For `number`, to catch a typo before the run rather than after it. */
  min?: number;
  max?: number;
  step?: number;
  /** Shown beside the field. */
  unit?: string;
}

export interface StageParam {
  label: string;
  value: string;
  /** Where the running value comes from. */
  origin: ParamOrigin;
  /** The file and key, or file and line, this was read from. */
  source: string;
  /** A caveat worth reading before trusting the value. */
  note?: string;
  /** Present when a reader may change this. Only ever on `origin: "config"`. */
  edit?: ParamEdit;
}

export interface StageParamGroup {
  title: string;
  params: StageParam[];
  /** Shown under the group heading, for what the numbers do not say. */
  note?: string;
}

/** Human wording for each origin, used by the panels. */
export const ORIGIN_LABEL: Record<ParamOrigin, string> = {
  config: "from config",
  fixed: "fixed in runner",
  default: "library default",
};

export const ORIGIN_HINT: Record<ParamOrigin, string> = {
  config: "The runner reads this key from the pipeline YAML at run time.",
  fixed: "Written into the runner script. Editing the YAML will not change it.",
  default: "Nothing sets this; the underlying default is what runs.",
};

/* ------------------------------------------------------------ brain mask --- */
/**
 * SynthStrip, shared by both pipelines' gen_mask stage. The conform settings
 * are the reason this stage is slow on CPU: the volume is resampled to 1 mm
 * isotropic and padded to a multiple of 64 before a single forward pass, so a
 * 4 mm-slice clinical DWI becomes a ~192³ volume regardless of how thin it was.
 */
const SYNTHSTRIP: StageParamGroup[] = [
  {
    title: "Brain extraction",
    params: [
      {
        label: "Method",
        value: "synthstrip",
        origin: "fixed",
        source: "run_ads_dwi.py → generate_brain_mask(…)",
        note: "brain_extraction.mask_option in the YAML still offers 'otsu'; the runner never reads it.",
      },
      {
        label: "Weights",
        value: "assets/models/synthstrip.1.pt",
        origin: "default",
        source: "ads/core/brainmask.py:196",
        note: "The nocsf variant is selected by no_csf=True, which no caller passes.",
      },
      { label: "Exclude CSF", value: "false", origin: "default", source: "ads/core/brainmask.py:163 no_csf" },
      {
        label: "Use GPU",
        value: "true (requested)",
        origin: "fixed",
        source: "run_ads_dwi.py → use_gpu=True",
        note: "Honoured only for CUDA. On Apple Silicon ADS Desk routes this through Metal; without that it falls to CPU.",
      },
    ],
  },
  {
    title: "Conform (inside SynthStrip)",
    note: "Applied to the input before inference and undone afterwards. Not configurable from anywhere.",
    params: [
      { label: "Voxel size", value: "1.0 mm isotropic", origin: "default", source: "ads/core/brainmask.py:212" },
      { label: "Orientation", value: "LIA", origin: "default", source: "ads/core/brainmask.py:212" },
      { label: "Resample method", value: "nearest", origin: "default", source: "ads/core/brainmask.py:212" },
      { label: "Crop", value: "to bounding box", origin: "default", source: "ads/core/brainmask.py:212" },
      {
        label: "Padded shape",
        value: "ceil(dim / 64) × 64, clipped to 192…320",
        origin: "default",
        source: "ads/core/brainmask.py:213",
      },
      { label: "Intensity scaling", value: "min-shift, then ÷ 99th percentile, clipped to 0…1", origin: "default", source: "ads/core/brainmask.py:218" },
      { label: "Mask rule", value: "signed distance < 0", origin: "default", source: "ads/core/brainmask.py:226" },
      { label: "Frames", value: "each frame masked separately", origin: "default", source: "ads/core/brainmask.py:207" },
    ],
  },
];

/* ---------------------------------------------------------- skull strip --- */
const SKULL_STRIP: StageParamGroup[] = [
  {
    title: "Masking",
    note: "A multiply, with no thresholds or smoothing of its own.",
    params: [
      {
        label: "Mask",
        value: "{id}_DWIbrain-mask.nii.gz",
        origin: "fixed",
        source: "run_ads_dwi.py → perform_skull_stripping(mask_path=…)",
      },
      {
        label: "Images stripped",
        value: "DWI, ADC, B0, stroke (each if present)",
        origin: "fixed",
        source: "run_ads_dwi.py → image_paths",
        note: "A missing B0 or reference stroke mask is skipped silently rather than failing the stage.",
      },
      { label: "Output suffix", value: "_brain", origin: "fixed", source: "ads/pipelines/preprocessing_brain_masking.py" },
    ],
  },
];

/* ---------------------------------------------------------- registration --- */
/**
 * Affine then SyN to the JHU MNI template. The spec object carries fields that
 * look like SyN settings but are never handed to ANTs — shrink factors,
 * smoothing sigmas, gradient step, metric and sampling reach the affine call
 * only (ants_io.py:136-150), so SyN runs on ANTsPy's own schedule for those.
 */
const ANTS_TO_MNI: StageParamGroup[] = [
  {
    title: "Target and transforms",
    params: [
      {
        label: "Template (DWI)",
        value: "JHU_MNI_SS_DWI_mask_to_MNI.nii",
        origin: "fixed",
        source: "run_ads_dwi.py → TemplatePaths.dwi_mask_template",
      },
      {
        label: "Template (ADC)",
        value: "JHU_MNI_SS_ADC_ss_to_MNI.nii",
        origin: "fixed",
        source: "run_ads_dwi.py → TemplatePaths.adc_template",
      },
      { label: "Template space", value: "MNI152, 1 mm", origin: "fixed", source: "assets/atlases/JHU_ICBM" },
      { label: "Stages", value: "Affine, then SyN on the affine result", origin: "fixed", source: "ads/pipelines/registration_align.py" },
      {
        label: "Interpolation",
        value: "linear; nearestNeighbor for masks",
        origin: "default",
        source: "ads/services/registration.py:59",
        note: "Applies to the mask and the reference stroke label, so neither gains intermediate values.",
      },
    ],
  },
  {
    title: "Affine",
    note: "Every field below is passed through to ants.registration.",
    params: [
      { label: "Transform", value: "Affine", origin: "default", source: "ads/domain/registration_spec.py:9" },
      {
        label: "Iterations",
        value: "100, 50, 25",
        origin: "default",
        source: "ads/domain/registration_spec.py:10",
        note: "defaults.yaml advertises 1000, 500, 250, 100 and is merged into the config, but the runner builds AffineSpec() without consulting it.",
      },
      { label: "Shrink factors", value: "8, 4, 2", origin: "default", source: "ads/domain/registration_spec.py:11" },
      { label: "Smoothing sigmas", value: "3.0, 2.0, 1.0", origin: "default", source: "ads/domain/registration_spec.py:12" },
      { label: "Gradient step", value: "0.1", origin: "default", source: "ads/domain/registration_spec.py:13" },
      { label: "Metric", value: "mattes", origin: "default", source: "ads/domain/registration_spec.py:14" },
      { label: "Sampling", value: "32 bins", origin: "default", source: "ads/domain/registration_spec.py:15" },
      { label: "Verbose", value: "true", origin: "fixed", source: "run_ads_dwi.py → AffineSpec(verbose=True)" },
    ],
  },
  {
    title: "SyN",
    note: "Only these three reach ANTs. The spec's shrink factors, sigmas, gradient step, metric and sampling are dropped at ants_io.py:139.",
    params: [
      { label: "Transform", value: "SyN", origin: "default", source: "ads/domain/registration_spec.py:22" },
      { label: "Iterations", value: "70, 50, 20", origin: "default", source: "ads/domain/registration_spec.py:23" },
      { label: "Verbose", value: "true", origin: "fixed", source: "run_ads_dwi.py → SyNSpec(verbose=True)" },
      { label: "Everything else", value: "ANTsPy defaults for type_of_transform='SyN'", origin: "default", source: "ants_io.py:139-150" },
    ],
  },
];

/* ------------------------------------------------------------------ DWI --- */
const DWI_PARAMS: Partial<Record<StageId, StageParamGroup[]>> = {
  prepdata: [
    {
      title: "Diffusion",
      params: [
        {
          label: "b-value",
          value: "1000 s/mm²",
          origin: "config",
          source: "dwi_pipeline.yaml → data_raw.bvalue",
          note: "Used to compute ADC from DWI and b0 when no ADC is supplied.",
          edit: { key: "data_raw.bvalue", kind: "number", fallback: 1000, min: 0, max: 5000, step: 50, unit: "s/mm²" },
        },
        {
          label: "Force ADC calculation",
          value: "false",
          origin: "config",
          source: "dwi_pipeline.yaml → data_raw.force_adc_calc",
          note: "true recomputes ADC from DWI/b0 even when the scanner's own ADC map is present.",
          edit: { key: "data_raw.force_adc_calc", kind: "boolean", fallback: false },
        },
        {
          label: "Detect DWI/b0 channel order",
          value: "true",
          origin: "config",
          source: "dwi_pipeline.yaml → data_raw.detect_dwi_b0_chann",
          note: "Read at preprocessing_dwi_prepare.py:52, defaulting to true. The shipped YAML omits the key, so it is settable but invisible there. It decides which volume of a 4D DWI is the b0.",
          edit: { key: "data_raw.detect_dwi_b0_chann", kind: "boolean", fallback: true },
        },
        {
          label: "Use b0",
          value: "— (has no effect)",
          origin: "fixed",
          source: "dwi_pipeline.yaml → data_raw.use_b0",
          note: "This key appears nowhere in the OpenADS Python. A b0 is used when the file exists, whatever it is set to.",
        },
      ],
    },
    {
      title: "Input discovery",
      note: "Left empty, files are found by name under the subject folder. Set one to point a role at a specific file.",
      params: [
        {
          label: "dwi_path",
          value: "(unset — found by name)",
          origin: "config",
          source: "dwi_pipeline.yaml → data_raw.dwi_path",
          edit: { key: "data_raw.dwi_path", kind: "text", fallback: "" },
        },
        {
          label: "adc_path",
          value: "(unset — found by name)",
          origin: "config",
          source: "dwi_pipeline.yaml → data_raw.adc_path",
          edit: { key: "data_raw.adc_path", kind: "text", fallback: "" },
        },
        {
          label: "b0_path",
          value: "(unset — found by name)",
          origin: "config",
          source: "dwi_pipeline.yaml → data_raw.b0_path",
          edit: { key: "data_raw.b0_path", kind: "text", fallback: "" },
        },
        {
          label: "stroke_path",
          value: "(unset — found by name)",
          origin: "config",
          source: "dwi_pipeline.yaml → data_raw.stroke_path",
          note: "A reference lesion mask. Supplying it is what makes the Dice and volume-error metrics non-null.",
          edit: { key: "data_raw.stroke_path", kind: "text", fallback: "" },
        },
      ],
    },
  ],
  gen_mask: SYNTHSTRIP,
  skull_strip: SKULL_STRIP,
  registration: ANTS_TO_MNI,
  inference: [
    {
      title: "Model",
      params: [
        {
          label: "Weights",
          value: "assets/models/DAGMNet_CH3.pth",
          origin: "config",
          source: "dwi_pipeline.yaml → model.path",
          note: "The one model parameter the runner actually reads, so this is where a retrained DAGMNet goes.",
          edit: { key: "model.path", kind: "text", fallback: "" },
        },
        {
          label: "Architecture",
          value: "DAGMNet, 3 channels",
          origin: "fixed",
          source: "run_ads_dwi.py → ModelConfig(architecture='dagmnet', n_channels=3)",
          note: "model.name, model.architecture and model.channels in the YAML are ignored; the runner hardcodes 'DAGMNet_DWI' and 3.",
        },
        { label: "Channels", value: "DWI, ADC, Prob_IS", origin: "fixed", source: "run_ads_dwi.py → spec.inference.n_channel = 3" },
        {
          label: "Device",
          value: "cuda if available, else cpu",
          origin: "fixed",
          source: "run_ads_dwi.py → spec.inference.device",
          note: "ADS Desk substitutes Metal here on Apple Silicon; the substitution was verified to change no metric.",
        },
      ],
    },
    {
      title: "Inference",
      params: [
        { label: "Target shape", value: "192 × 224 × 192", origin: "default", source: "ads/domain/segmentation_spec.py:40" },
        {
          label: "Downsampling factor",
          value: "2",
          origin: "default",
          source: "ads/domain/segmentation_spec.py:41",
          note: "DAGMNet's second, coarser input scale.",
        },
        { label: "Mask threshold", value: "0.50", origin: "default", source: "ads/domain/segmentation_spec.py:42" },
        {
          label: "Prediction threshold",
          value: "0.50",
          origin: "default",
          source: "ads/domain/segmentation_spec.py:43",
          note: "Where the probability map becomes the binary lesion. Changing it changes every reported volume.",
        },
      ],
    },
    {
      title: "Ischemic probability map (Prob_IS)",
      note: "The third input channel: a z-scored asymmetry map, computed from DWI and ADC before the network sees them.",
      params: [
        { label: "Smoothing FWHM", value: "2.0", origin: "default", source: "ads/domain/segmentation_spec.py:15" },
        { label: "DWI alpha / lambda", value: "1.5 / 4.0", origin: "default", source: "ads/domain/segmentation_spec.py:16-17" },
        { label: "ADC alpha / lambda", value: "0.5 / 2.0", origin: "default", source: "ads/domain/segmentation_spec.py:18-19" },
        { label: "z threshold", value: "2.0", origin: "default", source: "ads/domain/segmentation_spec.py:20" },
      ],
    },
    {
      title: "Post-processing",
      params: [
        { label: "Enabled", value: "true", origin: "default", source: "ads/domain/segmentation_spec.py:60" },
        { label: "Remove small objects", value: "true", origin: "default", source: "ads/domain/segmentation_spec.py:61" },
        {
          label: "Minimum object size",
          value: "5 voxels",
          origin: "default",
          source: "ads/domain/segmentation_spec.py:62",
          note: "In MNI space, so 5 mm³.",
        },
        {
          label: "Compute metrics",
          value: "true",
          origin: "fixed",
          source: "run_ads_dwi.py → spec.compute_metrics = True",
          note: "Produces {id}_metrics.json. Null throughout unless a reference stroke mask was supplied.",
        },
        { label: "Save normalised inputs", value: "false", origin: "fixed", source: "run_ads_dwi.py → spec.save_normalized_inputs = False" },
      ],
    },
  ],
  report: [
    {
      title: "Quantified fractional volume",
      note: "One CSV per atlas, plus a lesion-load CSV. Volumes are stored as log10(mL), not mL.",
      params: [
        {
          label: "Atlas directory",
          value: "assets/atlases/JHU_ICBM",
          origin: "config",
          source: "defaults.yaml → paths.templates.root (merged into the pipeline config)",
          edit: { key: "paths.templates.root", kind: "text", fallback: "" },
        },
        {
          label: "Atlases",
          value: "Vascular, Lobe, ASPECTS, ASPECTS-PCA, Ventricles, BMS, BPM Type I",
          origin: "fixed",
          source: "ads/pipelines/reporting_qfv_calculation.py:303",
        },
        {
          label: "Lookup tables",
          value: "vascular, lobe, aspects, lvor, lvir, bmos, bmis, aspectpc, bpm_type1",
          origin: "default",
          source: "reporting_qfv_calculation.py:49 get_LookupTables(LOOKUPS=…)",
        },
        {
          label: "Anatomy models",
          value: "assets/models/AA_models",
          origin: "config",
          source: "defaults.yaml → paths.models.aa_models (merged into the pipeline config)",
        },
      ],
    },
    {
      title: "Report",
      params: [
        { label: "Hydrocephalus section", value: "false", origin: "fixed", source: "run_ads_dwi.py → ReportPipeline(…).run(hydro_flag=False)" },
        {
          label: "Figures",
          value: "MNI comparison and native comparison",
          origin: "fixed",
          source: "run_ads_dwi.py → create_visualization_compare / _orig",
          note: "A failure here is logged as a warning; the stage still reports success.",
        },
        {
          label: "Lesion name",
          value: "— (has no effect)",
          origin: "fixed",
          source: "dwi_pipeline.yaml → reporting.lesion_name",
          note: "Neither reporting.lesion_name nor reporting.save_mni is read by the runner or by ReportPipeline.",
        },
      ],
    },
    {
      title: "Cleanup (after the last stage)",
      note: "Runs on success only, and deletes hard. Anything not matched by the manifest is removed from the output tree.",
      params: [
        {
          label: "Enabled",
          value: "true",
          origin: "config",
          source: "defaults.yaml → cleanup.enabled",
          note: "The Cleanup checkbox on the run form writes this key too.",
          edit: { key: "cleanup.enabled", kind: "boolean", fallback: true },
        },
        {
          label: "Keep manifest",
          value: "configs/keep_files.yaml",
          origin: "config",
          source: "defaults.yaml → cleanup.keep_manifest",
          edit: { key: "cleanup.keep_manifest", kind: "text", fallback: "" },
        },
      ],
    },
  ],
};

/* ------------------------------------------------------------------ PWI --- */
/**
 * The PWI runner is further from its YAML than the DWI one. Of the
 * pwi_preprocessing block only `motion_correction` is merged into the config
 * object (run_ads_pwi.py:322); slice timing, Gd concentration and TTP peaks
 * keep the dataclass defaults, which differ from the YAML in one place —
 * slice_order_code. Inference no longer uses the 3DUNet the YAML describes.
 */
const PWI_PARAMS: Partial<Record<StageId, StageParamGroup[]>> = {
  prepdata: [
    {
      title: "Slice timing",
      params: [
        {
          label: "Slice order code",
          value: "2 — interleaved ascending, even first",
          origin: "default",
          source: "ads/pipelines/preprocessing_pwi_genttp.py:39",
          note: "pwi_pipeline.yaml says 3. It is not merged, so 2 is what runs.",
        },
        {
          label: "Interpolation degree",
          value: "1 (linear)",
          origin: "default",
          source: "preprocessing_pwi_genttp.py:40",
          note: "Spline degree, 1 to 5.",
        },
      ],
    },
    {
      title: "Diffusion (shared with DWI)",
      params: [
        {
          label: "b-value",
          value: "1000 s/mm²",
          origin: "config",
          source: "pwi_pipeline.yaml → data_raw.bvalue",
          note: "Read at run_ads_pwi.py:522 when the PWI run has to prepare its own ADC.",
          edit: { key: "data_raw.bvalue", kind: "number", fallback: 1000, min: 0, max: 5000, step: 50, unit: "s/mm²" },
        },
      ],
    },
  ],
  gen_mask: SYNTHSTRIP,
  skull_strip: SKULL_STRIP,
  gen_ttp: [
    {
      title: "Motion correction",
      note: "The one block of the YAML the PWI runner merges, so these are live.",
      params: [
        {
          label: "Enabled",
          value: "true",
          origin: "config",
          source: "pwi_pipeline.yaml → …motion_correction.motion_correction",
          edit: { key: "pwi_preprocessing.motion_correction.motion_correction", kind: "boolean", fallback: true },
        },
        {
          label: "Transform",
          value: "rigid",
          origin: "config",
          source: "pwi_pipeline.yaml → …registration_params.mode",
          edit: {
            key: "pwi_preprocessing.motion_correction.registration_params.mode",
            kind: "enum",
            fallback: "rigid",
            choices: ["rigid", "affine"],
          },
        },
        {
          label: "Level iterations",
          value: "100, 10",
          origin: "config",
          source: "pwi_pipeline.yaml → …registration_params.level_iters",
          edit: {
            key: "pwi_preprocessing.motion_correction.registration_params.level_iters",
            kind: "numbers",
            fallback: [100, 10],
          },
        },
        {
          label: "Sigmas",
          value: "3.0, 1.0",
          origin: "config",
          source: "pwi_pipeline.yaml → …registration_params.sigmas",
          note: "The config writes these as floats. A value changed here is sent as typed, so 3 rather than 3.0 — dipy casts either way.",
          edit: {
            key: "pwi_preprocessing.motion_correction.registration_params.sigmas",
            kind: "numbers",
            fallback: [3.0, 1.0],
          },
        },
        {
          label: "Shrink factors",
          value: "4, 2",
          origin: "config",
          source: "pwi_pipeline.yaml → …registration_params.factors",
          edit: {
            key: "pwi_preprocessing.motion_correction.registration_params.factors",
            kind: "numbers",
            fallback: [4, 2],
          },
        },
        {
          label: "Timeout",
          value: "180 s",
          origin: "config",
          source: "pwi_pipeline.yaml → …motion_correction.timeout_seconds",
          edit: {
            key: "pwi_preprocessing.motion_correction.timeout_seconds",
            kind: "number",
            fallback: 180,
            min: 0,
            max: 3600,
            step: 30,
            unit: "s",
          },
        },
        {
          label: "On timeout",
          value: "true — skip motion correction and continue",
          origin: "config",
          source: "pwi_pipeline.yaml → …motion_correction.skip_if_timeout",
          note: "The stage still succeeds. The log line is the only record that the series went uncorrected.",
          edit: { key: "pwi_preprocessing.motion_correction.skip_if_timeout", kind: "boolean", fallback: true },
        },
        {
          label: "Save corrected PWI",
          value: "false",
          origin: "config",
          source: "pwi_pipeline.yaml → …motion_correction.save_corrected_PWI",
          edit: { key: "pwi_preprocessing.motion_correction.save_corrected_PWI", kind: "boolean", fallback: false },
        },
      ],
    },
    {
      title: "Gd concentration",
      note: "Dataclass defaults. The YAML repeats these values but is not merged, so editing it changes nothing.",
      params: [
        { label: "Spatial FWHM", value: "2.35", origin: "default", source: "preprocessing_pwi_genttp.py:72" },
        { label: "Temporal FWHM", value: "3.53", origin: "default", source: "preprocessing_pwi_genttp.py:73" },
        { label: "Baseline volumes", value: "6", origin: "default", source: "preprocessing_pwi_genttp.py:76" },
        { label: "Adaptive baseline", value: "true, at least 10% of volumes", origin: "default", source: "preprocessing_pwi_genttp.py:77-78" },
        { label: "Skip volumes", value: "1 at the start", origin: "default", source: "preprocessing_pwi_genttp.py:79" },
        { label: "Trim outliers", value: "1 high, 1 low", origin: "default", source: "preprocessing_pwi_genttp.py:80-81" },
        {
          label: "Echo time",
          value: "0.03 s",
          origin: "default",
          source: "preprocessing_pwi_genttp.py:84",
          note: "Assumed, not read from the sidecar. A series with a different TE is converted to concentration on the wrong scale.",
        },
      ],
    },
    {
      title: "TTP peak detection",
      note: "Dataclass defaults; the YAML copy is not merged.",
      params: [
        { label: "Peaks", value: "3", origin: "default", source: "preprocessing_pwi_genttp.py:90" },
        { label: "Prominence", value: "1", origin: "default", source: "preprocessing_pwi_genttp.py:91" },
        { label: "Height", value: "2", origin: "default", source: "preprocessing_pwi_genttp.py:92" },
        { label: "Width", value: "3", origin: "default", source: "preprocessing_pwi_genttp.py:93" },
      ],
    },
    {
      title: "Without a PWI sidecar",
      params: [
        {
          label: "Fallback",
          value: "motion correction off, TTP from repaired timings",
          origin: "fixed",
          source: "run_ads_pwi.py → _run_gen_ttp_with_json_fallback",
          note: "Taken when no {id}_PWI.json is found. TR is inferred, and the log says so.",
        },
      ],
    },
  ],
  registration: [
    {
      title: "PWI-specific",
      params: [
        {
          label: "Always register",
          value: "false — reuse the DWI transforms",
          origin: "config",
          source: "pwi_pipeline.yaml → registration.always_register",
          note: "Read at run_ads_pwi.py:479. false symlinks the DWI run's transforms, which requires the DWI half to have run.",
          edit: { key: "registration.always_register", kind: "boolean", fallback: false },
        },
        {
          label: "Write manifest",
          value: "false",
          origin: "fixed",
          source: "run_ads_pwi.py → RegistrationSpec(write_manifest=False)",
          note: "So a PWI-only run leaves no registration manifest behind, unlike DWI.",
        },
      ],
    },
    ...ANTS_TO_MNI,
  ],
  ttpadc_coreg: [
    {
      title: "Coregistration",
      params: [
        {
          label: "Raw TTP masked",
          value: "true — downstream uses {id}_TTP_space-DWI.nii.gz",
          origin: "config",
          source: "pwi_pipeline.yaml → registration.raw_ttp_masked",
          note: "Read at run_ads_pwi.py:671. false switches to the desc-brain variant.",
          edit: { key: "registration.raw_ttp_masked", kind: "boolean", fallback: true },
        },
        { label: "Transform", value: "Affine, TTP to ADC", origin: "fixed", source: "ads/domain/pwi_registration_spec.py:8" },
        {
          label: "ADC template",
          value: "JHU_MNI_SS_ADC_ss_to_MNI.nii",
          origin: "config",
          source: "defaults.yaml → paths.templates.root (merged into the pipeline config)",
        },
      ],
    },
  ],
  inference: [
    {
      title: "Model",
      note: "What runs is DAGMNet on four channels. The 3DUNet_PWI_Ch4 path in the YAML belongs to a block commented out at run_ads_pwi.py:731-748.",
      params: [
        {
          label: "Weights",
          value: "assets/models/dagmnet_nostroke.pth",
          origin: "fixed",
          source: "run_ads_pwi.py → checkpoint_path",
          note: "An absolute path built from the checkout, not from model.path_ch4.",
        },
        { label: "Channels", value: "4 — DWI, ADC, TTP, brain mask", origin: "fixed", source: "run_ads_pwi.py → DAGMNetPredictor(n_channels=4)" },
        {
          label: "Threshold",
          value: "0.49",
          origin: "fixed",
          source: "run_ads_pwi.py → DAGMNetPredictor(threshold=0.49)",
          note: "Where the hypoperfusion probability becomes the binary HP mask. inference_params.threshold (0.50) in the YAML is not read.",
        },
        {
          label: "Device",
          value: "cuda:1 → CPU on this machine",
          origin: "fixed",
          source: "run_ads_pwi.py → device",
          note: "A second GPU, hardcoded. DAGMNetPredictor falls back to CPU when CUDA is absent (dagmnet_pwi.py:834), and the Metal shim does not reach this call site — it redirects InferenceSpec.device, which this path does not use. Confirmed in the run log: 'CUDA not available, falling back to CPU'. Perfusion inference runs on CPU even with Metal on.",
        },
        { label: "Auto-generate symmetric-classic mask", value: "true", origin: "fixed", source: "run_ads_pwi.py → auto_generate_symclassic=True" },
        {
          label: "Allow missing channels",
          value: "true",
          origin: "fixed",
          source: "run_ads_pwi.py → allow_missing_channels=True",
          note: "A missing channel is zero-filled rather than refused, so check the log before trusting a mask.",
        },
        {
          label: "Minimum component size",
          value: "50 voxels",
          origin: "default",
          source: "ads/models/dagmnet_pwi.py:47 POST_MIN_SIZE",
          note: "Ten times the DWI stage's threshold, because hypoperfusion is a larger, smoother territory. dagmnet.post_min_size in the YAML is not passed to the predictor.",
        },
      ],
    },
    {
      title: "Not used",
      note: "Present in pwi_pipeline.yaml and reachable by nothing. Listed so nobody tunes them expecting an effect.",
      params: [
        { label: "model.name", value: "3DUNet_PWI_Ch4", origin: "fixed", source: "pwi_pipeline.yaml → model.name" },
        { label: "inference_params.input_size", value: "176 × 224 × 176", origin: "fixed", source: "pwi_pipeline.yaml → inference_params.input_size" },
        { label: "inference_params.normalize_ttp", value: "true", origin: "fixed", source: "pwi_pipeline.yaml → inference_params.normalize_ttp" },
        { label: "dagmnet.device", value: "cuda:1", origin: "fixed", source: "pwi_pipeline.yaml → dagmnet.device" },
      ],
    },
  ],
  report: [
    {
      title: "Hypoperfusion QFV",
      params: [
        {
          label: "Atlas directory",
          value: "assets/atlases/JHU_ICBM",
          origin: "config",
          source: "defaults.yaml → paths.templates.root (merged into the pipeline config)",
        },
        {
          label: "Atlases",
          value: "Vascular, Lobe, ASPECTS, ASPECTS-PCA, BPM Type I",
          origin: "fixed",
          source: "ads/pipelines/reporting_pwi_qfv_calculation.py",
        },
        {
          label: "Tables",
          value: "{id}_HPQFV.csv (fraction), {id}_HPload.csv (voxels)",
          origin: "fixed",
          source: "reporting_pwi_qfv_calculation.py",
        },
        {
          label: "Mismatch",
          value: "hypoperfusion minus core, from the two masks",
          origin: "fixed",
          source: "reporting_pwi_qfv_calculation.py",
          note: "Needs the DWI half. A PWI-only run reports hypoperfusion with no ratio.",
        },
      ],
    },
    {
      title: "Cleanup (after the last stage)",
      note: "Runs on success only, and deletes hard.",
      params: [
        {
          label: "Enabled",
          value: "true",
          origin: "config",
          source: "defaults.yaml → cleanup.enabled",
          note: "The Cleanup checkbox on the run form writes this key too.",
          edit: { key: "cleanup.enabled", kind: "boolean", fallback: true },
        },
        {
          label: "Keep manifest",
          value: "configs/keep_files.yaml",
          origin: "config",
          source: "defaults.yaml → cleanup.keep_manifest",
          edit: { key: "cleanup.keep_manifest", kind: "text", fallback: "" },
        },
      ],
    },
  ],
};

/** Empty array = this stage has no parameters worth naming. */
export function stageParams(modality: Modality, id: StageId): StageParamGroup[] {
  const table = modality === "DWI" ? DWI_PARAMS : PWI_PARAMS;
  return table[id] ?? [];
}

/** How many parameters a stage exposes, for the count beside a stage name. */
export function stageParamCount(modality: Modality, id: StageId): number {
  return stageParams(modality, id).reduce((n, g) => n + g.params.length, 0);
}

/**
 * Parameters whose value cannot be changed by editing the pipeline YAML.
 * Worth calling out: a config key that is silently ignored is the one thing a
 * reader of the YAML alone cannot discover.
 */
export function unreachableParams(
  modality: Modality,
  id: StageId,
): Array<{ group: string; param: StageParam }> {
  return stageParams(modality, id).flatMap((group) =>
    group.params.filter((p) => p.origin !== "config").map((param) => ({ group: group.title, param })),
  );
}

/** The parameters of one stage a reader may change. */
export function editableParams(modality: Modality, id: StageId): StageParam[] {
  return stageParams(modality, id).flatMap((g) => g.params.filter((p) => p.edit));
}

/**
 * Every settable parameter of a pipeline, so a run can show what it is about to
 * apply without walking the table again. Keyed by the dotted config path, which
 * is what the run carries — the same key may appear under two stages (`bvalue`
 * is read by both pipelines' prepdata) and it is one value either way.
 */
export function settableKeys(modality: Modality): Map<string, StageParam> {
  const table = modality === "DWI" ? DWI_PARAMS : PWI_PARAMS;
  const out = new Map<string, StageParam>();
  for (const groups of Object.values(table)) {
    for (const group of groups ?? []) {
      for (const param of group.params) {
        if (param.edit && !out.has(param.edit.key)) out.set(param.edit.key, param);
      }
    }
  }
  return out;
}

/** Render a value the way the field should show it. */
export function formatParamValue(value: ParamValue): string {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

/**
 * Read a typed value back out of a field, or a reason it cannot be used.
 *
 * Refusing here rather than at the runner keeps a typo from costing a
 * nine-minute run: `parse` is what the panel calls on every keystroke.
 */
export function parseParamValue(
  edit: ParamEdit,
  raw: string,
): { value: ParamValue } | { error: string } {
  const text = raw.trim();
  switch (edit.kind) {
    case "boolean":
      if (text === "true") return { value: true };
      if (text === "false") return { value: false };
      return { error: "must be true or false" };
    case "number": {
      if (text === "") return { error: "needs a number" };
      const n = Number(text);
      if (!Number.isFinite(n)) return { error: "not a number" };
      if (edit.min != null && n < edit.min) return { error: `must be at least ${edit.min}` };
      if (edit.max != null && n > edit.max) return { error: `must be at most ${edit.max}` };
      return { value: n };
    }
    case "numbers": {
      const parts = text.split(/[,\s]+/).filter((p) => p.length > 0);
      if (parts.length === 0) return { error: "needs at least one number" };
      const nums = parts.map(Number);
      if (nums.some((n) => !Number.isFinite(n))) return { error: "comma-separated numbers only" };
      return { value: nums };
    }
    case "enum":
      if (!edit.choices?.includes(text)) return { error: `must be one of ${edit.choices?.join(", ")}` };
      return { value: text };
    case "text":
      // An empty path means "leave it to the pipeline", which is a real choice
      // and the reason a blank text field is not an error.
      return { value: text };
  }
}
